import { type ReactNode, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import LinkIcon from '@atlaskit/icon/core/link';
import PageIcon from '@atlaskit/icon/core/page';
import TagIcon from '@atlaskit/icon/core/tag';
import VideoIcon from '@atlaskit/icon/core/video';
import { LoomLogo } from '@atlaskit/logo';
import { Radio as AkRadio } from '@atlaskit/radio';
import { type RadioProps } from '@atlaskit/radio/types';
import { token } from '@atlaskit/tokens';
import { JiraBackground } from '@atlassian/product-wireframes';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Modal } from '@/components/signup/layouts/modal';
import { Image } from '@/libraries/design/components/image/src/image';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import { useChoiceRedirect } from './choice-urls';
import loomCollaborationPanel from './assets/loom-collaboration-panel.png';
import loomOnlyPanel from './assets/loom-only.png';
import twcOthersLogos from './assets/twc-others.svg';
import { DEFAULT_TWC_PRICE, messages } from './messages';

/**
 * TWCG-2108 Loom Land opt-in — "Variant A" (TWC choice) design.
 *
 * Renders the "Loom works better in a collection" choice card. It behaves as an
 * accordion: the card whose radio is selected expands to show its feature list
 * and its own call-to-action. Matches the Figma "TWC _ Variant A" frames
 * (default = Teamwork Collection selected; Loom-only selected state) and follows
 * the shipped opt-in pattern (CFIND-5357 `jira-plus.tsx`): Modal chrome, product
 * logos, a component-viewed analytics event, and i18n copy.
 */

export type ChoiceValue = 'teamwork-collection' | 'loom-only';

const CONTENT_TEST_ID = 'loom-land-opt-in-variant-a';

// Figma "TWC _ Variant A" content height (px). Both columns share this height so
// the even 50/50 split reads as a full-height panel.
const MODAL_HEIGHT = 600;

// Splits the modal into the interactive content (left) and the marketing
// graphic panel (right) as an even 50/50 layout, matching the 968x600 Figma
// "TWC _ Variant A" frame. Fills the full modal width so the graphic panel
// bleeds to the modal's right edge (no white gutter); the "md" modal (972px)
// is already ~the Figma 968px width.
const layoutCss = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	// minHeight (not a hard height) lets the content breathe if it ever exceeds
	// 600px, so no scrollbar appears.
	minHeight: `${MODAL_HEIGHT}px`,
	width: '100%',
});

const contentCss = css({
	display: 'flex',
	flexDirection: 'column',
	// Figma spaces the title/cards block and the legal text apart within the
	// full height.
	justifyContent: 'space-between',
	gap: token('space.250'),
	// Matches Figma "Content" frame padding: 64px top, 48px sides/bottom.
	padding: `${token('space.800')} ${token('space.600')} ${token('space.600')}`,
	// Even 50/50 split with the graphic panel. An explicit 50% basis (plus
	// minWidth:0) prevents the cards' intrinsic width from making this column
	// wider than the panel.
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	boxSizing: 'border-box',
});

// TODO(TWCG-2108): Post-experiment polish — decompose the baked full-panel PNGs.
// Today each selection swaps a full ~484x600 PNG that has the blue field, swirl
// curve, and square cross-hatch grid baked in, so the shared background is
// duplicated per variant. The scalable approach is to layer the background as
// reusable SVGs (swirl + grid on the Blue200 field) and overlay only the
// lightweight illustration (cards) per variant on top. That makes adding new
// variants a single small illustration asset, keeps the background editable in
// one place, and enables theming (e.g. dark mode) without re-exporting panels.
//
// Right-hand graphic panel — the other half of the even split. Uses the full
// Figma "Graphic (Right side)" export (Blue200 field, white "swirl" shape, blue
// square cross-hatch texture and the illustration all baked into one 484x601
// asset), so it's pixel-matched to the design. Hidden on narrow viewports so
// the cards stay usable.
const graphicPanelCss = css({
	display: 'none',
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	alignSelf: 'stretch',
	overflow: 'hidden',
	backgroundColor: wacToken('color.extended.blue200'),
	// The panel image is applied inline (see the panel element) so it can swap
	// between the collaboration and Loom-only illustrations based on selection.
	// Fit the full asset width so nothing is cropped horizontally (the
	// right-edge "Summarize meeting" pill always stays visible). Anchored to the
	// top; the Blue200 backgroundColor fills any area below the art so the field
	// still reads as continuous.
	backgroundSize: '100% auto',
	backgroundPosition: 'top center',
	backgroundRepeat: 'no-repeat',
	'@media (min-width: 768px)': {
		display: 'block',
	},
});

// The Figma title is a single line of large display text. `text-wrap: balance`
// keeps it tidy if it ever must wrap on very narrow content, but at the 484px
// content width the "large" heading fits on one line.
const headingCss = css({
	textWrap: 'balance',
});

const cardsCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	// A fixed (not min) height keeps the block exactly the same size regardless
	// of which card is expanded, so toggling options never shifts the modal
	// layout. Sized to fit the taller (4-feature) expanded TWC card plus the
	// collapsed card and the gap, with no extra dead space.
	height: '414px',
});

const cardCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	padding: token('space.250'),
	borderRadius: 'var(--ds-border-radius-100, 4px)',
	borderWidth: 'var(--ds-border-width, 1px)',
	borderStyle: 'solid',
	borderColor: token('color.border'),
});

// Indents the expanded card body (subtitle, feature list, CTA) so it aligns
// under the header text rather than the radio control. The offset equals the DS
// Radio control width plus its label gap (~24px), matching the Figma layout.
const cardBodyCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	paddingInlineStart: token('space.300'),
});

const selectedCardCss = css({
	borderColor: token('color.border.selected'),
});

type CardRadioProps = {
	name: string;
	value: ChoiceValue;
	isChecked: boolean;
	onChange: (value: ChoiceValue) => void;
	ariaLabel: string;
	children: React.ReactNode;
};

// Uses the DS `@atlaskit/radio` so the control matches the design system exactly
// (blue ring with a white center dot when selected). The custom header (logos /
// "only" text) is passed as the radio's `label`, keeping the whole row clickable
// and accessible without a hand-rolled input.
// The DS `Radio` is a forwardRef component whose JSX signature, under this repo's
// @types/react, demands DOM-only attributes like `crossOrigin` /
// `onPointerEnterCapture` as required props (TS2739). Casting it to a
// ComponentType keyed on the DS's own `RadioProps` restores the intended
// (all-optional) prop contract. This mirrors the `as unknown as
// React.ComponentType<...>` pattern used elsewhere in signup/.
const Radio = AkRadio as unknown as React.ComponentType<RadioProps>;

const CardRadio = ({ name, value, isChecked, onChange, ariaLabel, children }: CardRadioProps) => (
	<Radio
		name={name}
		value={value}
		isChecked={isChecked}
		ariaLabel={ariaLabel}
		onChange={() => onChange(value)}
		label={children}
	/>
);

const logosCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	// DS body typography, then reset to regular weight: the DS Radio label
	// applies a medium/bold weight to its text, but the card header should read
	// lighter (the Loom wordmark keeps its own bold styling via the logo SVG).
	font: token('font.body'),
	fontWeight: token('font.weight.regular'),
});

// The "+" between the Loom logo and the app-tile cluster in the TWC card header.
const plusCss = css({
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
});

// Combined product-logos graphic (Jira, Confluence, Trello, Rovo) shown after
// the "+" in the TWC card header, provided as a single SVG asset from design.
// Sized to match the Loom wordmark height beside it.
const appTilesCss = css({
	display: 'block',
	height: '30px',
	width: 'auto',
});

// Loom-only card header: bottom-align the row so the "only" qualifier sits on
// the bottom of the Loom wordmark rather than its vertical center.
const loomOnlyHeaderCss = css({
	alignItems: 'flex-end',
});

// The word "only" following the Loom wordmark in the Loom-only card header —
// regular weight and subtle so it reads as a qualifier next to the bold Loom
// wordmark (matching Figma), not a heavy label.
//
// The Loom "small" logo renders in a ~24px SVG box whose visible glyphs sit
// around the vertical centre, so simply flex-aligning the text to the row's
// bottom leaves it looking centred. Anchoring the text to the bottom of the row
// and nudging it down with a transform lands it on the wordmark's visual
// baseline.
const onlyLabelCss = css({
	alignSelf: 'flex-end',
	lineHeight: 1,
	transform: 'translateY(-3px)',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
});

const subtitleCss = css({
	margin: 0,
	// DS small body typography (12px) to match the card body text density in
	// Figma; medium weight so the subtitle reads as a mini-heading above the
	// feature list.
	font: token('font.body.small'),
	fontWeight: token('font.weight.medium'),
	color: token('color.text'),
});

const listCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	margin: 0,
	padding: 0,
	listStyle: 'none',
});

const listItemCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	// DS small body typography (12px) to match Figma's card body density and keep
	// long copy (e.g. "Access all apps for only $5 more per user /month") on a
	// single line. The icon stays a fixed size and the text takes the rest.
	font: token('font.body.small'),
	color: token('color.text.subtle'),
});

// Keeps the feature icon from shrinking (which would otherwise push text to
// wrap) and vertically aligned with the first line of text.
const listIconCss = css({
	flexShrink: 0,
	display: 'flex',
});

const listTextCss = css({
	minWidth: 0,
});

const legalCss = css({
	margin: 0,
	// DS small body typography (12px) — the Figma legal disclaimer size.
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

// Coerces `FormattedMessage` chunk children to a plain string. ICU passes the
// chunk content as an array; the messages here are always plain text, so this
// yields the link label safely — a non-primitive `ReactNode` can never render as
// "[object Object]" in `text` / `aria-label`.
const chunksToString = (chunks: ReactNode): string =>
	(Array.isArray(chunks) ? chunks : [chunks]).map((c) => (typeof c === 'string' ? c : '')).join('');

// Inline legal links. Defined at module scope (not during render) so they don't
// create unstable component types and so the a11y linter can see the anchor
// content. Each resolves its localized href via `useLocalizedHref`, following the
// established pattern in account-creation/.../terms-and-conditions.tsx.
export const CustomerAgreementLink = (chunks: ReactNode) => {
	const href = useLocalizedHref('/legal/atlassian-customer-agreement');
	const label = chunksToString(chunks);
	return <Link aria-label={label} href={href} text={label} type="inline" />;
};

export const PrivacyPolicyLink = (chunks: ReactNode) => {
	const href = useLocalizedHref('/legal/privacy-policy');
	const label = chunksToString(chunks);
	return <Link aria-label={label} href={href} text={label} type="inline" />;
};

export const CancelAnytimeLink = (chunks: ReactNode) => {
	const label = chunksToString(chunks);
	return (
		<Link
			aria-label={label}
			href="https://support.atlassian.com/subscriptions-and-billing/docs/cancel-a-subscription/"
			text={label}
			type="inline"
		/>
	);
};

// Legal disclaimer with inline links, matching the Figma copy for each choice.
// The CTA label is interpolated so the two variants share one message shape.
const LegalText = ({ isTwc }: { isTwc: boolean }) => (
	<p css={legalCss}>
		<FormattedMessage
			{...(isTwc ? messages.legalTwc : messages.legalLoomOnly)}
			values={{
				customerAgreement: CustomerAgreementLink,
				privacyPolicy: PrivacyPolicyLink,
				cancelAnytime: CancelAnytimeLink,
			}}
		/>
	</p>
);

// A feature row: the message descriptor plus its icon. `values` carries optional
// ICU arguments for messages that interpolate (e.g. the {price} pricing line).
type Feature = {
	message: MessageDescriptor;
	icon: JSX.Element;
	values?: Record<string, ReactNode>;
};

// Feature icons use the Atlassian brand blue to match Figma.
const featureIconColor = token('color.icon.brand');

export const buildTwcFeatures = (price: ReactNode): Feature[] => [
	{
		message: messages.twcFeatureEmbed,
		icon: <VideoIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.twcFeatureNotes,
		icon: <PageIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.twcFeaturePrice,
		icon: <TagIcon label="" color={featureIconColor} />,
		values: { price },
	},
	{
		message: messages.twcFeatureTrial,
		icon: <CalendarIcon label="" color={featureIconColor} />,
	},
];

const LOOM_ONLY_FEATURES: Feature[] = [
	{
		message: messages.loomOnlyFeatureRecord,
		icon: <VideoIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.loomOnlyFeatureRecaps,
		icon: <PageIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.loomOnlyFeatureShare,
		icon: <LinkIcon label="" color={featureIconColor} />,
	},
];

export const FeatureList = ({ features }: { features: Feature[] }) => (
	<ul css={listCss}>
		{features.map((feature) => (
			<li key={feature.message.id} css={listItemCss}>
				<span css={listIconCss}>{feature.icon}</span>
				<span css={listTextCss}>
					<FormattedMessage {...feature.message} values={feature.values} />
				</span>
			</li>
		))}
	</ul>
);

export type LoomChoiceVariantAProps = {
	/**
	 * Optional callback invoked when the user confirms a choice via a card CTA,
	 * before the redirect to signup/provisioning (e.g. for analytics). The CTA
	 * itself always navigates to the choice's continue URL — pass a custom
	 * {@link onSelect} only to hook additional behaviour, not to override routing.
	 */
	onSelect?: (choice: ChoiceValue) => void;
	/**
	 * Which option is selected on first render. Defaults to Teamwork Collection
	 * (the recommended path). Primarily useful for previewing each expanded state
	 * in isolation (e.g. Storybook).
	 */
	initialChoice?: ChoiceValue;
	/**
	 * Monthly per-user upsell price shown in the Teamwork Collection pricing line.
	 * Passed as an ICU argument so the amount (or currency) can change without
	 * editing the translated copy. Defaults to {@link DEFAULT_TWC_PRICE}.
	 */
	price?: ReactNode;
};

export const LoomChoiceVariantA = ({
	onSelect,
	initialChoice = 'teamwork-collection',
	price = DEFAULT_TWC_PRICE,
}: LoomChoiceVariantAProps) => {
	const [choice, setChoice] = useState<ChoiceValue>(initialChoice);
	const twcFeatures = buildTwcFeatures(price);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const redirectToChoice = useChoiceRedirect();

	// Confirming a choice fires the optional callback (analytics/side effects)
	// then navigates to that choice's signup/provisioning flow:
	//  - teamwork-collection → ?bundle=teamwork-collection&edition=premium
	//  - loom-only           → today's Loom-only flow (same as control redirect)
	const handleSelect = (selected: ChoiceValue) => {
		onSelect?.(selected);
		redirectToChoice(selected);
	};

	// Fire the modal "viewed" screen event once the card is shown. The `variant`
	// attribute differentiates the arm ('a' vs 'b'), so a single screen event
	// covers both variants for the funnel/experiment dashboards — no separate
	// component-viewed event needed. Guarded on analyticsClient so it safely
	// no-ops outside the live signup flow (e.g. Storybook).
	useEffect(() => {
		if (!analyticsClient) {
			return;
		}
		createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'modal',
			name: 'loomLandOptInModal',
			eventType: 'screen',
			attributes: { variant: 'a' },
		}).fire();
	}, [analyticsClient, createAnalyticsEvent]);

	const isTwc = choice === 'teamwork-collection';

	return (
		<JiraBackground>
			<Modal
				// "md" (972px) matches the Figma 968px frame far better than "lg"
				// (1140px), giving a near-exact 486/486 even split.
				size="md"
				productLogo={<LoomLogo appearance="brand" size="medium" shouldUseNewLogoDesign />}
			>
				<div css={layoutCss}>
					<div css={contentCss} data-testid={CONTENT_TEST_ID}>
						<div css={headingCss}>
							<Heading size="medium" as="h1">
								<FormattedMessage {...messages.heading} />
							</Heading>
						</div>

						<div css={cardsCss}>
							{/* Teamwork Collection card */}
							<div css={[cardCss, isTwc && selectedCardCss]}>
								<CardRadio
									name="loom-land-choice"
									value="teamwork-collection"
									isChecked={isTwc}
									onChange={setChoice}
									ariaLabel="Loom plus Jira, Confluence, Trello, Rovo"
								>
									<span css={logosCss}>
										<LoomLogo appearance="brand" size="small" shouldUseNewLogoDesign />
										<span css={plusCss} aria-hidden="true">
											+
										</span>
										<Image
											css={appTilesCss}
											src={twcOthersLogos}
											alt="Jira, Confluence, Trello, Rovo"
										/>
									</span>
								</CardRadio>

								{isTwc && (
									<div css={cardBodyCss}>
										<p css={subtitleCss}>
											<FormattedMessage {...messages.twcSubtitle} />
										</p>
										<FeatureList features={twcFeatures} />
										<Button
											appearance="primary"
											shouldFitContainer
											onClick={() => handleSelect('teamwork-collection')}
										>
											<FormattedMessage {...messages.twcCta} />
										</Button>
									</div>
								)}
							</div>

							{/* Loom-only card */}
							<div css={[cardCss, !isTwc && selectedCardCss]}>
								<CardRadio
									name="loom-land-choice"
									value="loom-only"
									isChecked={!isTwc}
									onChange={setChoice}
									ariaLabel="Loom only"
								>
									<span css={[logosCss, loomOnlyHeaderCss]}>
										<LoomLogo appearance="brand" size="small" shouldUseNewLogoDesign />
										<span css={onlyLabelCss}>
											<FormattedMessage {...messages.loomOnlyLabel} />
										</span>
									</span>
								</CardRadio>

								{!isTwc && (
									<div css={cardBodyCss}>
										<p css={subtitleCss}>
											<FormattedMessage {...messages.loomOnlySubtitle} />
										</p>
										<FeatureList features={LOOM_ONLY_FEATURES} />
										<Button shouldFitContainer onClick={() => handleSelect('loom-only')}>
											<FormattedMessage {...messages.loomOnlyCta} />
										</Button>
									</div>
								)}
							</div>
						</div>

						<LegalText isTwc={isTwc} />
					</div>

					<div
						css={graphicPanelCss}
						role="presentation"
						style={{
							backgroundImage: `url(${isTwc ? loomCollaborationPanel : loomOnlyPanel})`,
						}}
					/>
					{/* Screen-reader-only equivalent of the decorative panel could go
					    here if the illustration ever conveys unique info. */}
				</div>
			</Modal>
		</JiraBackground>
	);
};
